/** Derive a registrable hostname from website / URL-ish input (Lead Account fields). */

function stripLeadingWww(host: string): string {
  return host.replace(/^www\./i, "");
}

function fallbackHostFromLooseInput(raw: string): string | null {
  let s = raw.trim().toLowerCase();
  if (!s || /\s/.test(s) || s.includes("@")) return null;

  s = s.replace(/^https?:\/\//i, "");
  s = s.split(/[/?#]/)[0] ?? "";
  s = s.replace(/:\d+$/, "");
  s = stripLeadingWww(s);
  s = s.replace(/^\.+|\.+$/g, "");

  return s || null;
}

/** Hostname from website URL or bare domain; null when input cannot be parsed. */
export function deriveDomainFromWebsite(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed);
  if (hasScheme) {
    try {
      const host = stripLeadingWww(new URL(trimmed).hostname.toLowerCase());
      return host || null;
    } catch {
      return fallbackHostFromLooseInput(trimmed);
    }
  }

  try {
    const host = stripLeadingWww(new URL(`https://${trimmed}`).hostname.toLowerCase());
    return host || null;
  } catch {
    return fallbackHostFromLooseInput(trimmed);
  }
}

/** Trim and lowercase manual domain input (does not strip paths; user typed a hostname). */
export function normalizeDomainFieldInput(raw: string): string {
  return raw.trim().toLowerCase().replace(/^\.+|\.+$/g, "");
}

/** Hostname shape for company domain (requires at least one dot, e.g. example.com). */
export function isPlausibleDomainHostname(raw: string): boolean {
  const d = normalizeDomainFieldInput(raw);
  if (!d || d.length > 253 || d.includes("@") || d.includes(" ") || d.includes("/")) {
    return false;
  }
  return /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(
    d,
  );
}

export function isPlausibleWebsiteInput(raw: string): boolean {
  const s = raw.trim();
  if (!s) return true;
  if (/\s/.test(s)) return false;
  const derived = deriveDomainFromWebsite(s);
  return derived !== null && isPlausibleDomainHostname(derived);
}

export type LeadAccountWebFieldValues = {
  website: string;
  domain: string;
};

/** Derived-until-dirty: sync domain from website when empty or still equal to last auto value. */
export function nextDomainAfterWebsiteChange(
  website: string,
  currentDomain: string,
  lastAutoDerivedDomain: string,
): { domain: string; lastAutoDerivedDomain: string } {
  const trimmedWebsite = website.trim();
  if (!trimmedWebsite) {
    const cur = currentDomain.trim();
    const last = lastAutoDerivedDomain.trim();
    if (!cur || cur.toLowerCase() === last.toLowerCase()) {
      return { domain: "", lastAutoDerivedDomain: "" };
    }
    return { domain: currentDomain, lastAutoDerivedDomain: lastAutoDerivedDomain };
  }

  const derived = deriveDomainFromWebsite(trimmedWebsite) ?? "";
  const curNorm = currentDomain.trim().toLowerCase();
  const lastNorm = lastAutoDerivedDomain.trim().toLowerCase();
  if (!curNorm || curNorm === lastNorm) {
    return { domain: derived, lastAutoDerivedDomain: derived };
  }
  return { domain: currentDomain, lastAutoDerivedDomain: lastAutoDerivedDomain };
}

export function validateLeadAccountWebFields(
  values: LeadAccountWebFieldValues,
): string | null {
  const website = values.website.trim();
  const domain = values.domain.trim();

  if (website && !isPlausibleWebsiteInput(website)) {
    return "Enter a valid website URL or domain (for example https://example.com or example.com).";
  }
  if (domain && !isPlausibleDomainHostname(domain)) {
    return "Enter a valid company domain (for example example.com).";
  }
  return null;
}
